import { httpClient, toClientApiError } from "./httpClient";

export type CustomerStatus = "active" | "frozen" | "cancelled";

export type Customer = {
  id: string;
  code: number | null;
  name: string;
  canonicalName: string;
  status: string;
  regulatoryId: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  cityKey: string | null;
  communityName: string | null;
  generalCommunity: string | null;
  logoUrl: string | null;
  joinDate: string | null;
  cancelDate: string | null;
  cancelReasonKey: string | null;
  sourceKey: string | null;
  commPrefKey: string | null;
  lastOutreachOn: string | null;
  notes: string | null;
  failedCharges: number;
  contact: string | null;
  contactName: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  billingMethodKey: string | null;
  mandateActive: boolean | null;
  mandateExpiryOn: string | null;
  payLast4: string | null;
  billingDay: number | null;
  monthlyAmount: number | null;
  lastChargeStatusKey: string | null;
  lastChargeOn: string | null;
  totalPaid: number | null;
  balanceDue: number | null;
  lastPaymentDate: string | null;
  flagMandateExpiring: boolean;
  flagDebt: boolean;
  daysSincePayment: number | null;
  tenureDays: number | null;
  flagAtRisk: boolean;
  attributes: Record<string, unknown>;
  products: Record<string, string>;
  activeProducts?: Array<{ key: string; labelHe: string; color: string | null }>;
  createdAt: string;
  updatedAt: string;
};

export type CustomerListCatalogColumn = {
  fieldKey: string;
  labelHe: string;
  type: string;
  defaultHidden: boolean;
  filterable?: boolean;
  sortable?: boolean;
};

export type CustomerListResult = {
  items: Customer[];
  total: number;
  page: number;
  pageSize: number;
  catalog?: {
    columns: CustomerListCatalogColumn[];
    defaultHiddenColumnKeys: string[];
    defaultVisibleKeys: string[];
  };
};

/** the quick cards of the customers table (server: customerTable.CUSTOMER_QUICK_KEYS) */
export type CustomerQuickKey = "active" | "frozen" | "cancelled" | "debt" | "mandateExpiring" | "atRisk" | "newThisMonth";

/** GET /customers/table — a page of the server-mode DataTable, with the quick-card counts and footer sums (agorot) */
export type CustomersTablePage = CustomerListResult & {
  quickCounts: Record<"all" | CustomerQuickKey, number>;
  sums: { totalPaid: number; balanceDue: number; monthlyAmount: number };
};

export type CustomersTableParams = Record<string, string | number | undefined>;

export type LookupList = {
  listKey: string;
  labelHe: string;
  options: Array<{ key: string; labelHe: string }>;
};

export type CustomerContact = {
  id: string;
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
  phone: string | null;
  email: string | null;
  status: string;
  roleLabel: string | null;
  roleKey?: string | null;
  linkedAt: string;
};

export type Project = {
  id: string;
  name: string;
  kind: string;
  status: string;
  customerId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ActivityItem = {
  id: string;
  eventType: string;
  actorUserId: string | null;
  result: string;
  metadata: Record<string, unknown>;
  createdAt: string;
};

export type CustomerAlias = {
  id: string;
  customerId: string;
  alias: string;
  canonicalAlias: string;
  createdAt: string;
  updatedAt: string;
};

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

const cleanParams = (params: CustomersTableParams) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ""));

export const customersApi = {
  list(query?: {
    q?: string;
    status?: string;
    filters?: Record<string, unknown>;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortDirection?: "asc" | "desc";
  }): Promise<Customer[]> {
    return wrap(async () => {
      const result = await customersApi.listPage(query);
      return result.items;
    });
  },

  listPage(query?: {
    q?: string;
    status?: string;
    filters?: Record<string, unknown>;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortDirection?: "asc" | "desc";
  }): Promise<CustomerListResult> {
    return wrap(async () => {
      const res = await httpClient.get<CustomerListResult>("/customers", {
        params: {
          q: query?.q,
          status: query?.status,
          filters: query?.filters ? JSON.stringify(query.filters) : undefined,
          page: query?.page,
          pageSize: query?.pageSize ?? 200,
          sortField: query?.sortField,
          sortDirection: query?.sortDirection,
        },
      });
      return res.data;
    });
  },

  /**
   * The customers list as a server-mode DataTable (Turbo contract): q · quick · vf_ / condop_ column filters ·
   * sortBy / sortDir · page / limit (5000 with export=1). `list` / `listPage` stay for the other screens.
   */
  table(params: CustomersTableParams): Promise<CustomersTablePage> {
    return wrap(async () => {
      const res = await httpClient.get<CustomersTablePage>("/customers/table", { params: cleanParams(params) });
      return res.data;
    });
  },

  /** distinct values of one column (its filter); the other filters apply */
  tableColumnValues(params: CustomersTableParams): Promise<{ items: Array<{ value: string; count: number }> }> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: Array<{ value: string; count: number }> }>("/customers/table/column-values", {
        params: cleanParams(params),
      });
      return res.data;
    });
  },

  listLookups(listKey?: string): Promise<LookupList[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: LookupList[] }>("/lookups", {
        params: { listKey },
      });
      return res.data.items;
    });
  },

  get(id: string): Promise<Customer> {
    return wrap(async () => {
      const res = await httpClient.get<{ item: Customer }>(`/customers/${id}`);
      return res.data.item;
    });
  },

  create(input: {
    name: string;
    phone?: string;
    email?: string;
    address?: string;
    regulatoryId?: string;
    cityKey?: string;
    communityName?: string;
    joinDate?: string;
    contactFirst?: string;
    contactLast?: string;
    contactPhone?: string;
    sourceKey?: string;
    billingMethodKey?: string;
  }): Promise<Customer> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: Customer }>("/customers", input);
      return res.data.item;
    });
  },

  update(
    id: string,
    input: {
      name?: string;
      status?: CustomerStatus;
      phone?: string | null;
      email?: string | null;
      address?: string | null;
      regulatoryId?: string | null;
      cityKey?: string | null;
      communityName?: string | null;
      generalCommunity?: string | null;
      logoUrl?: string | null;
      joinDate?: string | null;
      cancelDate?: string | null;
      cancelReasonKey?: string | null;
      sourceKey?: string | null;
      commPrefKey?: string | null;
      lastOutreachOn?: string | null;
      notes?: string | null;
      attributes?: Record<string, unknown>;
      billing?: {
        billingMethodKey?: string | null;
        mandateExpiryOn?: string | null;
        payLast4?: string | null;
        billingDay?: number | null;
        lastChargeStatusKey?: string | null;
        lastChargeOn?: string | null;
        monthlyAmountAgorot?: number | null;
      };
      products?: Record<string, "active" | "cancelled" | "">;
    },
  ): Promise<Customer> {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: Customer }>(
        `/customers/${id}`,
        input,
      );
      return res.data.item;
    });
  },

  archive(
    id: string,
    input?: { cancelReasonKey?: string },
  ): Promise<Customer> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: Customer }>(
        `/customers/${id}/archive`,
        input ?? {},
      );
      return res.data.item;
    });
  },

  importCsv(csv: string): Promise<{
    created: number;
    contactsCreated: number;
    skipped: number;
    errors: Array<{ line: number; message: string }>;
    storageKey: string;
  }> {
    return wrap(async () => {
      const res = await httpClient.post<{
        result: {
          created: number;
          contactsCreated: number;
          skipped: number;
          errors: Array<{ line: number; message: string }>;
          storageKey: string;
        };
      }>("/customers/import", { csv });
      return res.data.result;
    });
  },

  previewImportFile(file: File): Promise<{
    storageKey: string;
    format: "csv" | "xlsx";
    totalRows: number;
    fieldHeaders: string[];
    unmappedHeaders: string[];
    previewRows: Array<{
      line: number;
      name: string;
      phone: string;
      email: string;
      contactName: string;
      status: "ok" | "error";
      message?: string;
    }>;
    estimatedCreate: number;
    estimatedSkip: number;
    errors: Array<{ line: number; message: string }>;
  }> {
    return wrap(async () => {
      const form = new FormData();
      form.append("file", file);
      const res = await httpClient.post<{
        preview: {
          storageKey: string;
          format: "csv" | "xlsx";
          totalRows: number;
          fieldHeaders: string[];
          unmappedHeaders: string[];
          previewRows: Array<{
            line: number;
            name: string;
            phone: string;
            email: string;
            contactName: string;
            status: "ok" | "error";
            message?: string;
          }>;
          estimatedCreate: number;
          estimatedSkip: number;
          errors: Array<{ line: number; message: string }>;
        };
      }>("/customers/import/preview", form, {
        headers: { "Content-Type": undefined },
        transformRequest: [
          (data, headers) => {
            if (headers && typeof headers === "object" && "delete" in headers) {
              (headers as { delete: (key: string) => void }).delete("Content-Type");
            } else if (headers && typeof headers === "object") {
              delete (headers as Record<string, unknown>)["Content-Type"];
              delete (headers as Record<string, unknown>)["content-type"];
            }
            return data;
          },
        ],
      });
      return res.data.preview;
    });
  },

  commitImport(storageKey: string): Promise<{
    created: number;
    contactsCreated: number;
    skipped: number;
    errors: Array<{ line: number; message: string }>;
    storageKey: string;
  }> {
    return wrap(async () => {
      const res = await httpClient.post<{
        result: {
          created: number;
          contactsCreated: number;
          skipped: number;
          errors: Array<{ line: number; message: string }>;
          storageKey: string;
        };
      }>("/customers/import/commit", { storageKey });
      return res.data.result;
    });
  },

  listContacts(customerId: string): Promise<CustomerContact[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: CustomerContact[] }>("/contacts", {
        params: { customerId },
      });
      return res.data.items;
    });
  },

  createContact(input: {
    displayName: string;
    phone?: string;
    email?: string;
    customerId: string;
    roleLabel?: string;
    roleKey?: "primary" | "secondary" | "reference";
  }): Promise<{ id: string; displayName: string }> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: { id: string; displayName: string } }>(
        "/contacts",
        input,
      );
      return res.data.item;
    });
  },

  listProjects(customerId: string): Promise<Project[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: Project[] }>("/projects", {
        params: { customerId },
      });
      return res.data.items;
    });
  },

  createProject(input: {
    name: string;
    kind?: "unit" | "program";
    customerId: string;
  }): Promise<Project> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: Project }>("/projects", input);
      return res.data.item;
    });
  },

  listActivity(customerId: string): Promise<ActivityItem[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: ActivityItem[] }>(
        `/customers/${customerId}/activity`,
      );
      return res.data.items;
    });
  },

  listAliases(customerId: string): Promise<CustomerAlias[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: CustomerAlias[] }>(
        `/customers/${customerId}/aliases`,
      );
      return res.data.items;
    });
  },

  addAlias(customerId: string, alias: string): Promise<CustomerAlias> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: CustomerAlias }>(
        `/customers/${customerId}/aliases`,
        { alias },
      );
      return res.data.item;
    });
  },

  deleteAlias(customerId: string, aliasId: string): Promise<CustomerAlias> {
    return wrap(async () => {
      const res = await httpClient.delete<{ item: CustomerAlias }>(
        `/customers/${customerId}/aliases/${aliasId}`,
        { data: {} },
      );
      return res.data.item;
    });
  },

  merge(
    sourceId: string,
    targetCustomerId: string,
  ): Promise<{ source: Customer; target: Customer }> {
    return wrap(async () => {
      const res = await httpClient.post<{
        source: Customer;
        target: Customer;
      }>(`/customers/${sourceId}/merge`, { targetCustomerId });
      return res.data;
    });
  },
};
